import * as THREE from 'three';
import type { BuildingLot } from '../types';

/**
 * Occluder cut-away (DRIVING-2 / CITY-6). Buildings, roofs, street furniture and tree canopies are
 * merged per chunk, so they cannot be faded one object at a time. Instead the shared city materials
 * get a small fragment patch: any fragment that lies inside a capsule around the line of sight from
 * the camera to the focus (the player, or the car they drive) and is nearer the camera than the
 * focus is screen-door dithered away. The player stays visible behind tall frontages, warehouse
 * roofs and plane-tree canopies, while the rest of the building still draws (and still casts its
 * shadow: the shadow pass uses the depth material, which is not patched).
 *
 * One set of uniforms is shared by every patched material; `update()` is called once per frame.
 */
export interface OccluderFade {
  /**
   * Add the cut-away to a material (call before its first compile). With `thinTagged`, vertices whose
   * (otherwise unused) uv is {@link OCCLUDER_THIN_UV} get a narrower capsule: thin iron (lamp posts,
   * signal poles, railings, crane members) loses a short section rather than a 4 m one, and a 10 cm post
   * never hid the player anyway. The props material has no map, so its uvs are free for the tag.
   */
  patch(mat: THREE.Material, thinTagged?: boolean): void;
  /**
   * Add only the hole's night fill to a ground material (the ground is never cut). `worldVarying` names
   * the world-position varying the material already has (the ground's water cut provides `vWorldPosCut`).
   */
  patchFill(mat: THREE.Material, worldVarying: string): void;
  /** The building lots the sight line is tested against: the hole only gets its fill when one is cut. */
  setBlockers(lots: readonly BuildingLot[]): void;
  /** How dark the borough is (0 by day, 1 at night): scales the hole's fill. */
  setFillLevel(level: number): void;
  /** Camera position and the world point that must stay visible; `radius` is the clear radius (m). */
  update(camera: THREE.Vector3, focus: THREE.Vector3, radius: number): void;
  /** Turn the cut-away off (menus, cinematics without a focus). */
  disable(): void;
  /** Exposed for tests / debugging. */
  readonly uniforms: OccluderUniforms;
}

export interface OccluderUniforms {
  uOccCam: { value: THREE.Vector3 };
  uOccFocus: { value: THREE.Vector3 };
  uOccRadius: { value: number };
  uOccOn: { value: number };
  /** The hole's fill: the night level × how much a building is being cut (eased). */
  uOccFill: { value: number };
}

/**
 * Fraction of fragments removed at the centre of the cut. It used to be 0.86, leaving a "ghost" of every
 * seventh pixel, but from the high camera that ghost read as a halftone screen laid over the frontage
 * and as dotted strips down lamp posts. The core is now a clean cut; only its rim is dithered.
 */
export const OCCLUDER_STRENGTH = 1.0;
/** The dithered rim, as a fraction of the clear radius (inside it the cut is solid). */
export const OCCLUDER_RIM = 0.9;
/** Length (m) of the dithered ramp along the sight line, just in front of the margin. */
export const OCCLUDER_RAMP = 0.45;
/** Fragments within this many metres (along the sight line) of the focus are never cut. */
export const OCCLUDER_MARGIN = 1.2;
/** Capsule radius scale for thin things (`metal`: lamp posts, signal poles, railings, crane members). */
export const OCCLUDER_THIN = 0.4;
/** The uv a thin vertex carries (the `metal` pieces merged into the `props` chunks; see cityView). */
export const OCCLUDER_THIN_UV = -8;
/**
 * The hole's fill at night. A building cut in front of the player left them standing in a near-black
 * well (no street lamp reaches the yard behind a frontage, and the building still casts its shadow).
 * What shows through the hole near the focus gets a faint lift of its own colour, so the floor reads
 * as a dim interior and the player stands out against it. It only comes on while the sight line goes
 * through a building lot, only inside the clear radius and only near the focus: it never lights the street.
 */
export const OCCLUDER_FILL: readonly [number, number, number] = [0.5, 0.46, 0.4];
/** The fill fades out over this many metres past the focus, along the sight line. */
export const OCCLUDER_FILL_DEPTH = 4;

const UNIFORMS_GLSL = /* glsl */ `
uniform vec3 uOccCam;
uniform vec3 uOccFocus;
uniform float uOccRadius;
uniform float uOccOn;
uniform float uOccFill;
`;

const FRAG_HEADER = /* glsl */ `
varying vec3 vOccWorld;
varying float vOccThin;
${UNIFORMS_GLSL}
float bhOccBayer2(vec2 a) {
  a = floor(a);
  return fract(dot(a, vec2(0.5, a.y * 0.75)));
}
// Ordered-dither threshold in [0,1) on a 4x4 pixel pattern.
float bhOccBayer(vec2 p) {
  return bhOccBayer2(0.5 * p) * 0.25 + bhOccBayer2(p);
}
`;

const FRAG_CUT = /* glsl */ `
  if (uOccOn > 0.5) {
    vec3 occD = uOccFocus - uOccCam;
    float occLen = length(occD);
    vec3 occU = occD / max(occLen, 1e-3);
    float occT = dot(vOccWorld - uOccCam, occU);
    float occAhead = occLen - occT; // metres in front of the focus, towards the camera
    if (occT > 0.0 && occAhead > ${OCCLUDER_MARGIN.toFixed(2)}) {
      float occR = uOccRadius * mix(1.0, ${OCCLUDER_THIN.toFixed(2)}, vOccThin);
      float occDist = length(vOccWorld - (uOccCam + occU * occT));
      float occF = (1.0 - smoothstep(occR * ${OCCLUDER_RIM.toFixed(2)}, occR, occDist)) * smoothstep(${OCCLUDER_MARGIN.toFixed(2)}, ${(OCCLUDER_MARGIN + OCCLUDER_RAMP).toFixed(2)}, occAhead);
      // threshold kept just above zero so a fully cut fragment always goes and an uncut one always stays
      if (occF * ${OCCLUDER_STRENGTH.toFixed(2)} > bhOccBayer(gl_FragCoord.xy) * 0.98 + 0.01) discard;
    }
  }
`;

/**
 * The hole's fill, after the emissive map: a lift of the surface's own colour near the focus only. The
 * distance from the sight line is taken back to the focus's depth (the hole in a roof nearer the camera
 * opens a wider patch of floor than its own radius), and the fill reaches about 1.5 radii there, so it
 * covers what the hole shows and little more. On cut materials it stays off in front of the margin (the
 * rim's dithered remains must not glow) and fades out above 0.5–2.5 m: a floor fill, not a lamp on the
 * back wall. The ground is never cut, so the floor between the player and the cut wall is filled too.
 */
const fragFill = (world: string, ground: boolean): string => /* glsl */ `
  if (uOccOn > 0.5 && uOccFill > 0.003) {
    vec3 occD = uOccFocus - uOccCam;
    float occLen = length(occD);
    vec3 occU = occD / max(occLen, 1e-3);
    float occT = dot(${world} - uOccCam, occU);
    float occDist = length(${world} - (uOccCam + occU * occT)) * occLen / max(occT, 1.0);
    float occK = (1.0 - smoothstep(uOccRadius * 0.7, uOccRadius * 1.5, occDist))
      * (1.0 - smoothstep(0.0, ${OCCLUDER_FILL_DEPTH.toFixed(1)}, occT - occLen))${
        ground
          ? ''
          : `\n      * (1.0 - smoothstep(${OCCLUDER_MARGIN.toFixed(2)}, ${(OCCLUDER_MARGIN + OCCLUDER_RAMP).toFixed(2)}, occLen - occT))\n      * (1.0 - smoothstep(0.5, 2.5, ${world}.y)) * 0.45`
      };
    totalEmissiveRadiance += diffuseColor.rgb * vec3(${OCCLUDER_FILL.map((v) => v.toFixed(3)).join(', ')}) * (occK * uOccFill);
  }
`;

export function createOccluderFade(): OccluderFade {
  const uniforms: OccluderUniforms = {
    uOccCam: { value: new THREE.Vector3() },
    uOccFocus: { value: new THREE.Vector3() },
    uOccRadius: { value: 2.5 },
    uOccOn: { value: 0 },
    uOccFill: { value: 0 },
  };
  let blockers: OccluderBlocker[] = [];
  let fillLevel = 0;
  let cover = 0;
  return {
    uniforms,
    patch(mat, thinTagged = false) {
      const prev = mat.onBeforeCompile;
      const prevKey = mat.customProgramCacheKey.bind(mat);
      mat.onBeforeCompile = (shader, renderer) => {
        prev.call(mat, shader, renderer);
        Object.assign(shader.uniforms, uniforms);
        shader.vertexShader = shader.vertexShader
          .replace('#include <common>', '#include <common>\nvarying vec3 vOccWorld;\nvarying float vOccThin;')
          .replace(
            '#include <worldpos_vertex>',
            `#include <worldpos_vertex>\nvOccWorld = (modelMatrix * vec4(transformed, 1.0)).xyz;\nvOccThin = ${thinTagged ? `uv.x < ${(OCCLUDER_THIN_UV + 0.5).toFixed(1)} ? 1.0 : 0.0` : '0.0'};`,
          );
        shader.fragmentShader = shader.fragmentShader
          .replace('#include <common>', `#include <common>\n${FRAG_HEADER}`)
          .replace('void main() {', `void main() {\n${FRAG_CUT}`)
          .replace('#include <emissivemap_fragment>', `#include <emissivemap_fragment>\n${fragFill('vOccWorld', false)}`);
      };
      mat.customProgramCacheKey = () => `${prevKey()}|occ${thinTagged ? 'thin' : ''}`;
      mat.needsUpdate = true;
    },
    patchFill(mat, world) {
      const prev = mat.onBeforeCompile;
      const prevKey = mat.customProgramCacheKey.bind(mat);
      mat.onBeforeCompile = (shader, renderer) => {
        prev.call(mat, shader, renderer);
        Object.assign(shader.uniforms, uniforms);
        shader.fragmentShader = shader.fragmentShader
          .replace('#include <common>', `#include <common>\n${UNIFORMS_GLSL}`)
          .replace('#include <emissivemap_fragment>', `#include <emissivemap_fragment>\n${fragFill(world, true)}`);
      };
      mat.customProgramCacheKey = () => `${prevKey()}|occfill`;
      mat.needsUpdate = true;
    },
    setBlockers(lots) {
      blockers = lots.map(occluderBlocker);
    },
    setFillLevel(level) {
      fillLevel = Math.min(1, Math.max(0, level));
    },
    update(camera, focus, radius) {
      uniforms.uOccCam.value.copy(camera);
      uniforms.uOccFocus.value.copy(focus);
      uniforms.uOccRadius.value = radius;
      uniforms.uOccOn.value = 1;
      // Eased per frame, so the fill never pops in or out as the player steps behind a frontage.
      const target = fillLevel > 0.003 && sightCrossesLot(blockers, camera, focus, OCCLUDER_MARGIN) ? 1 : 0;
      cover += (target - cover) * 0.12;
      if (Math.abs(target - cover) < 0.002) cover = target;
      uniforms.uOccFill.value = cover * fillLevel;
    },
    disable() {
      uniforms.uOccOn.value = 0;
      uniforms.uOccFill.value = 0;
      cover = 0;
    },
  };
}

/**
 * CPU mirror of the shader test (used by tests and debugging): how strongly a world point is cut
 * for the given camera and focus (0 = drawn, 1 = fully cut before dithering).
 */
export function occluderCut(p: THREE.Vector3, camera: THREE.Vector3, focus: THREE.Vector3, radius: number, radiusScale = 1): number {
  const r = radius * radiusScale;
  const d = focus.clone().sub(camera);
  const len = d.length();
  const u = d.divideScalar(Math.max(len, 1e-3));
  const t = p.clone().sub(camera).dot(u);
  const ahead = len - t;
  if (t <= 0 || ahead <= OCCLUDER_MARGIN) return 0;
  const dist = p.clone().sub(camera.clone().addScaledVector(u, t)).length();
  const ss = (e0: number, e1: number, x: number): number => {
    const k = Math.min(1, Math.max(0, (x - e0) / (e1 - e0)));
    return k * k * (3 - 2 * k);
  };
  return (1 - ss(r * OCCLUDER_RIM, r, dist)) * ss(OCCLUDER_MARGIN, OCCLUDER_MARGIN + OCCLUDER_RAMP, ahead);
}

/** A building lot as the sight test wants it: centre, heading cos / sin, half extents, height, reach. */
export interface OccluderBlocker {
  x: number;
  z: number;
  c: number;
  s: number;
  hx: number;
  hz: number;
  h: number;
  r: number;
}

export function occluderBlocker(l: BuildingLot): OccluderBlocker {
  return { x: l.center.x, z: l.center.z, c: Math.cos(l.angle), s: Math.sin(l.angle), hx: l.halfX, hz: l.halfZ, h: l.height, r: Math.hypot(l.halfX, l.halfZ) };
}

/**
 * Whether the sight line from the camera to `margin` metres short of the focus goes through a building
 * lot (an oriented box from the ground to its height): the cut-away is then opening a hole in a building
 * and the floor under the player gets the night fill. A slab test per lot near the segment.
 */
export function sightCrossesLot(lots: readonly OccluderBlocker[], camera: THREE.Vector3, focus: THREE.Vector3, margin: number): boolean {
  const dx = focus.x - camera.x;
  const dy = focus.y - camera.y;
  const dz = focus.z - camera.z;
  const len = Math.hypot(dx, dy, dz);
  if (len <= margin) return false;
  const k = (len - margin) / len;
  const ex = camera.x + dx * k;
  const ey = camera.y + dy * k;
  const ez = camera.z + dz * k;
  const minX = Math.min(camera.x, ex);
  const maxX = Math.max(camera.x, ex);
  const minZ = Math.min(camera.z, ez);
  const maxZ = Math.max(camera.z, ez);
  const p0 = [0, 0, 0];
  const p1 = [0, 0, 0];
  for (const b of lots) {
    if (b.x + b.r < minX || b.x - b.r > maxX || b.z + b.r < minZ || b.z - b.r > maxZ) continue;
    // Into the lot's frame (heading convention: local x = (cos a, -sin a), local z = (sin a, cos a)).
    const ax = camera.x - b.x;
    const az = camera.z - b.z;
    const bx = ex - b.x;
    const bz = ez - b.z;
    p0[0] = ax * b.c - az * b.s;
    p0[1] = camera.y;
    p0[2] = ax * b.s + az * b.c;
    p1[0] = bx * b.c - bz * b.s;
    p1[1] = ey;
    p1[2] = bx * b.s + bz * b.c;
    const lo0 = -b.hx;
    const hi0 = b.hx;
    let t0 = 0;
    let t1 = 1;
    let hit = true;
    for (let i = 0; i < 3 && hit; i++) {
      const lo = i === 0 ? lo0 : i === 1 ? 0 : -b.hz;
      const hi = i === 0 ? hi0 : i === 1 ? b.h : b.hz;
      const o = p0[i]!;
      const d = p1[i]! - o;
      if (Math.abs(d) < 1e-9) {
        if (o < lo || o > hi) hit = false;
        continue;
      }
      let ta = (lo - o) / d;
      let tb = (hi - o) / d;
      if (ta > tb) [ta, tb] = [tb, ta];
      if (ta > t0) t0 = ta;
      if (tb < t1) t1 = tb;
      if (t0 > t1) hit = false;
    }
    if (hit) return true;
  }
  return false;
}
